/**
 * Dojo MVP feature flags — pure constants.
 *
 * No React, no tRPC, no DOM imports here so the file is unit-testable
 * in plain Node vitest.
 *
 * Static UI section visibility (HIDE_*). Default-on for the initial dojo
 * MVP — flip off when each subsystem is built:
 *   HIDE_SYNC          → cross-browser sync needs device-pairing
 *   HIDE_SECURITY_TAB  → user-side overview redundant with /review
 *   HIDE_LICENSES_TAB  → paid-licenses needs payments first
 *
 * Net-new feature gating now goes through PostHog flags (owner-controlled,
 * per-user rollout) rather than a server-side allow-list. See the cli-billing
 * flag for the pattern.
 */

// ─── Static section flags ──────────────────────────────────────────────

export const HIDE_SYNC = true
export const HIDE_SECURITY_TAB = true
export const HIDE_LICENSES_TAB = true
// Hide the home-page "My Extensions" tab (the user's *installed* extensions).
// There is no working way to scan installed extensions yet (the desktop app
// isn't ready), so the surface shows nothing useful. Flip to false once
// installed-extension scanning ships. The developer-facing Ownership tab
// becomes the default landing view while this is on (see app/page.tsx).
export const HIDE_MY_EXTENSIONS = true

// ─── PostHog feature flags ──────────────────────────────────────────────
//
// Runtime-controlled, per-user rollout flags evaluated against PostHog (EU
// instance). Unlike the static HIDE_* constants above, these let us pick who
// sees a feature from the PostHog UI (by distinct_id == user.id, an `email`/
// `role` person property, or a cohort — dojo calls posthog.identify(user.id,
// { email, role }) in auth-context, so all three targeting modes work).
//
// Register the key here before referencing it in code — single source of truth
// for what exists in PostHog vs the codebase. The React hook that evaluates
// these lives in ./use-feature-flag.ts (kept out of this React-free file so it
// stays unit-testable in plain Node).
export const FEATURE_FLAGS = {
	// Gates the Extenshi CLI billing surface (sidebar entry + /billing page).
	// Safe default = OFF: the feature stays hidden until explicitly enabled for
	// a user in PostHog. Owner-controlled access gate, not an A/B experiment.
	CLI_BILLING: 'cli-billing',

	// ── Remaining developer tool rollouts ──────────
	//
	// Uniform "show-gate" semantics: flag ON ⇒ tool shown. Each tool's
	// code-level default lives in TOOL_GATES below (covers the no-consent +
	// PostHog-loading windows). Default-visible tools must be created in
	// PostHog at 100% rollout, otherwise consented users lose them once PostHog
	// loads and returns false for the unconfigured flag.
	TOOL_MANIFEST_GENERATOR: 'tools-manifest-generator',
	TOOL_PRIVACY_POLICY_GENERATOR: 'tools-privacy-policy-generator',
	TOOL_INSTRUCTION_GENERATOR: 'tools-instruction-generator',
	// Install instructions — the per-browser install guide. Separate from the pin
	// guide above, which owns the `instruction-generator` slug for historical
	// reasons. Rolled out to 100% on 2026-09-27.
	TOOL_INSTALL_INSTRUCTIONS: 'tools-install-instructions',
	TOOL_PAGE_GENERATOR: 'tools-page-generator',
	TOOL_PUBLISH: 'tools-publish',
	TOOL_UNINSTALL_FEEDBACK: 'tools-uninstall-feedback',
	// AI visibility (llms.txt + JSON-LD). Local generator, no credits — default
	// visible like privacy-policy. ROADMAP it. 9 T2/T3.
	TOOL_AI_VISIBILITY: 'tools-ai-visibility',

	// BYO extension analytics: the "connect your analytics provider" card on
	// /integrations and the extensions/[id]/analytics dashboard. Dark-launched
	// at 0% with an operator override, per the MVP plan.
	//
	// Visual only. Integrations itself is generally available. Access control
	// stays where it belongs: catalog-bff refuses
	// every analytics procedure for an extension the caller has not verified
	// ownership of, flag or no flag.
	EXTENSION_ANALYTICS: 'new-extension-analytics',
} as const

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS] | (string & {})

// ─── Local-development flag override ────────────────────────────────────
//
// PostHog is never initialised locally: providers.tsx only calls posthog.init()
// when NEXT_PUBLIC_POSTHOG_KEY is set, and the dev secrets carry no PostHog
// key. Every flag therefore stays `undefined` forever, so a dark-launched
// feature is unreachable on a dev machine no matter
// how it is configured in PostHog.
//
// NEXT_PUBLIC_DEV_FEATURE_FLAGS is the escape hatch: a comma-separated list of
// flag keys to force ON, or '*' for all of them.
//
//   NEXT_PUBLIC_DEV_FEATURE_FLAGS=new-extension-analytics,tools-publish yarn dev
//
// Production can't be opened this way: the override is compiled out whenever
// NODE_ENV === 'production', which is what `next build` always sets.

/** Parse the override list; '*' matches every flag. */
export function parseDevFeatureFlags(raw: string | undefined): Set<string> {
	return new Set(
		(raw ?? '')
			.split(',')
			.map((part) => part.trim())
			.filter(Boolean),
	)
}

/**
 * `true` when this flag is force-enabled for local development, `undefined`
 * when the override does not apply (so the caller falls back to PostHog).
 *
 * Never returns `false`: the override only ever opens a gate, so a flag PostHog
 * has already answered `false` for is not silently flipped by a stale env var.
 */
export function devFeatureFlagOverride(
	key: FeatureFlagKey,
	env: { nodeEnv?: string; devFlags?: string } = {
		nodeEnv: process.env.NODE_ENV,
		devFlags: process.env.NEXT_PUBLIC_DEV_FEATURE_FLAGS,
	},
): true | undefined {
	if (env.nodeEnv === 'production') return undefined
	const flags = parseDevFeatureFlags(env.devFlags)
	return flags.has('*') || flags.has(key) ? true : undefined
}

/**
 * Per-tool gate config, keyed by the /tools/<slug> route segment.
 *
 * `defaultVisible` is the code-level decision used while PostHog has not
 * answered (analytics consent declined, or flags still loading). It is the
 * single source of truth shared by the Tools card listing (app/tools/page.tsx)
 * and the route gate (app/tools/layout.tsx) so they never disagree.
 *
 * Default visible (must be created in PostHog at 100% rollout):
 *   manifest-generator, privacy-policy-generator, uninstall-feedback, ai-visibility,
 *   page-generator, instruction-generator, install-instructions
 * Ungated tools (icon-generator, onboarding-page, feedback-form, seo-optimizer) have flag: null
 * and never consult PostHog.
 * Default hidden (create at 0%, then target users/roles/cohorts): publish
 */
export const TOOL_GATES = {
	'icon-generator': { flag: null, defaultVisible: true },
	'manifest-generator': { flag: FEATURE_FLAGS.TOOL_MANIFEST_GENERATOR, defaultVisible: true },
	'privacy-policy-generator': { flag: FEATURE_FLAGS.TOOL_PRIVACY_POLICY_GENERATOR, defaultVisible: true },
	'instruction-generator': { flag: FEATURE_FLAGS.TOOL_INSTRUCTION_GENERATOR, defaultVisible: true },
	'install-instructions': { flag: FEATURE_FLAGS.TOOL_INSTALL_INSTRUCTIONS, defaultVisible: true },
	'page-generator': { flag: FEATURE_FLAGS.TOOL_PAGE_GENERATOR, defaultVisible: true },
	publish: { flag: FEATURE_FLAGS.TOOL_PUBLISH, defaultVisible: false },
	'uninstall-feedback': { flag: FEATURE_FLAGS.TOOL_UNINSTALL_FEEDBACK, defaultVisible: true },
	'onboarding-page': { flag: null, defaultVisible: true },
	'feedback-form': { flag: null, defaultVisible: true },
	'seo-optimizer': { flag: null, defaultVisible: true },
	'ai-visibility': { flag: FEATURE_FLAGS.TOOL_AI_VISIBILITY, defaultVisible: true },
} as const

export type ToolSlug = keyof typeof TOOL_GATES

/** Defaults for the remaining PostHog-controlled tools. Ungated tools skip the SDK. */
const FEATURE_FLAG_DEFAULTS: Readonly<Record<string, boolean>> = Object.fromEntries(
	Object.values(TOOL_GATES)
		.filter(({ flag }) => flag !== null)
		.map(({ flag, defaultVisible }) => [flag, defaultVisible]),
)

export function featureFlagDefault(key: FeatureFlagKey): boolean {
	return FEATURE_FLAG_DEFAULTS[key] === true
}

/**
 * Tri-state a flag-gated *page or nav item* should render in.
 *
 *   enabled === true                       → 'enabled'  (show the feature)
 *   enabled === false                      → 'disabled' (PostHog says no)
 *   enabled === undefined + default ON     → 'enabled'  (GA stays available)
 *   enabled === undefined + default OFF    → 'loading' with consent, otherwise
 *                                             'disabled'
 *
 * The adapter discards cached PostHog answers after consent withdrawal.
 * Analytics never initializes without consent; GA features use their default
 * while restricted features remain hidden instead of waiting forever.
 */
export type FeatureGateState = 'loading' | 'enabled' | 'disabled'

export function deriveFeatureGateState(args: {
	enabled: boolean | undefined
	hasConsent: boolean
	defaultVisible?: boolean
}): FeatureGateState {
	if (args.enabled === true) return 'enabled'
	if (args.enabled === false) return 'disabled'
	if (args.defaultVisible) return 'enabled'
	return args.hasConsent ? 'loading' : 'disabled'
}

/**
 * Like deriveFeatureGateState, but `defaultVisible`-aware so a tool gate can be
 * default-ON. Used by the /tools route gate (app/tools/layout.tsx) where the
 * no-consent window must keep a default-visible tool reachable, yet must NOT
 * redirect a *targeted* user out before PostHog has answered.
 *
 *   enabled === true                          → 'enabled'  (PostHog says show)
 *   enabled === false                         → 'disabled' (PostHog says hide —
 *                                                even for a default-visible tool)
 *   enabled === undefined + no consent        → defaultVisible ? 'enabled'
 *                                                              : 'disabled'
 *                                                (PostHog will never load; fall
 *                                                 back to the code default)
 *   enabled === undefined + has consent       → defaultVisible ? 'enabled'
 *                                                              : 'loading'
 */
export function deriveToolGateState(args: {
	enabled: boolean | undefined
	hasConsent: boolean
	defaultVisible: boolean
}): FeatureGateState {
	return deriveFeatureGateState(args)
}
